import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export function TrafficAnalyticsFiltersDrawer({
  open,
  onClose,
  filters,
  setFilters,
  shops = [],
  onApply,
  onReset,
}) {
  const selectedShops =
    shops.filter((shop) =>
      filters.shop_ids?.includes(
        shop.id,
      ),
    );

  const allSelected =
    shops.length > 0 &&
    selectedShops.length ===
      shops.length;

  function toggleAll() {
    setFilters((current) => ({
      ...current,

      shop_ids: allSelected
        ? []
        : shops.map(
            (shop) => shop.id,
          ),
    }));
  }

  function handleFromDateChange(
    event,
  ) {
    const value =
      event.target.value;

    setFilters((current) => {
      const currentTo =
        current.date_to || "";

      /*
       * If the new from-date is
       * later than the current
       * to-date, move the to-date
       * forward automatically.
       */
      const nextTo =
        currentTo &&
        value &&
        currentTo < value
          ? value
          : currentTo;

      return {
        ...current,

        date_from: value,

        date_to: nextTo,
      };
    });
  }

  function handleToDateChange(
    event,
  ) {
    const value =
      event.target.value;

    setFilters((current) => ({
      ...current,

      date_to: value,
    }));
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: 460,
          },

          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 900,
          }}
        >
          Wi-Fi analytics filters
        </Typography>

        <Typography
          sx={{
            mt: 0.4,
            color: "#667085",
            fontSize: "0.7rem",
          }}
        >
          Select one or multiple shops
          and a reporting period.
        </Typography>
      </Box>

      <Divider />

      <Stack
        spacing={2}
        sx={{
          p: 2,
          overflowY: "auto",
        }}
      >
        {/* Shops */}

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 0.6 }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
              }}
            >
              Shops
            </Typography>

            <Button
              size="small"
              onClick={toggleAll}
              sx={{
                minWidth: 0,
                fontSize: "0.64rem",
              }}
            >
              {allSelected
                ? "Clear all"
                : "Select all"}
            </Button>
          </Stack>

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={shops}
            value={selectedShops}
            limitTags={3}
            getOptionLabel={(option) =>
              `${option.shop_name} (${option.shop_code})`
            }
            isOptionEqualToValue={(
              option,
              value,
            ) =>
              option.id === value.id
            }
            onChange={(_, selected) => {
              setFilters(
                (current) => ({
                  ...current,

                  shop_ids:
                    selected.map(
                      (shop) =>
                        shop.id,
                    ),
                }),
              );
            }}
            renderOption={(
              props,
              option,
              state,
            ) => (
              <li
                {...props}
                key={option.id}
              >
                <Checkbox
                  checked={
                    state.selected
                  }
                  size="small"
                  sx={{
                    p: 0.3,
                    mr: 1,
                  }}
                />

                <ListItemText
                  primary={
                    option.shop_name
                  }
                  secondary={`${option.shop_code}${
                    option.location
                      ? ` • ${option.location}`
                      : ""
                  }`}
                  primaryTypographyProps={{
                    fontSize:
                      "0.74rem",
                    fontWeight: 750,
                  }}
                  secondaryTypographyProps={{
                    fontSize:
                      "0.62rem",
                  }}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select shops"
                placeholder="Search shops"
                size="small"
              />
            )}
          />
        </Box>

        {/* Date range */}

        <Box>
          <Typography
            sx={{
              mb: 0.7,
              color: "#344054",
              fontSize: "0.68rem",
              fontWeight: 800,
            }}
          >
            Reporting period
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              fullWidth
              label="From date"
              type="date"
              size="small"
              value={
                filters.date_from ||
                ""
              }
              onChange={
                handleFromDateChange
              }
              slotProps={{
                inputLabel: {
                  shrink: true,
                },

                htmlInput: {
                  max:
                    filters.date_to ||
                    undefined,
                },
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  backgroundColor:
                    "#FFFFFF",

                  px: 0.5,
                },

                "& input": {
                  fontSize:
                    "0.78rem",
                },
              }}
            />

            <TextField
              fullWidth
              label="To date"
              type="date"
              size="small"
              value={
                filters.date_to ||
                ""
              }
              onChange={
                handleToDateChange
              }
              disabled={
                !filters.date_from
              }
              slotProps={{
                inputLabel: {
                  shrink: true,
                },

                htmlInput: {
                  min:
                    filters.date_from ||
                    undefined,
                },
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  backgroundColor:
                    "#FFFFFF",

                  px: 0.5,
                },

                "& input": {
                  fontSize:
                    "0.78rem",
                },
              }}
            />
          </Stack>

          {filters.date_from && (
            <Typography
              sx={{
                mt: 0.6,
                color: "#667085",
                fontSize: "0.6rem",
              }}
            >
              The end date cannot be
              earlier than the selected
              start date.
            </Typography>
          )}
        </Box>

        {/* Chart interval */}

        <FormControl
          size="small"
          fullWidth
        >
          <InputLabel>
            Chart interval
          </InputLabel>

          <Select
            value={
              filters.interval ||
              "day"
            }
            label="Chart interval"
            onChange={(event) =>
              setFilters(
                (current) => ({
                  ...current,

                  interval:
                    event.target.value,
                }),
              )
            }
          >
            <MenuItem value="hour">
              Hourly
            </MenuItem>

            <MenuItem value="day">
              Daily
            </MenuItem>

            <MenuItem value="week">
              Weekly
            </MenuItem>

            <MenuItem value="month">
              Monthly
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Divider />

      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 2 }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onReset}
          sx={{
            borderRadius: 0,
          }}
        >
          Reset
        </Button>

        <Button
          fullWidth
          variant="contained"
          disabled={
            !filters.shop_ids?.length ||
            !filters.date_from ||
            !filters.date_to
          }
          onClick={() => {
            onApply();
            onClose();
          }}
          sx={{
            borderRadius: 0,
          }}
        >
          Apply filters
        </Button>
      </Stack>
    </Drawer>
  );
}